import React, { useState, useMemo, useEffect } from 'react';
import { clsx } from 'clsx';
import {
  FunnelIcon,
  MagnifyingGlassIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  UsersIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import { Task, TaskStatus, TaskPriority } from '../../../types/task.types';
import { TaskCard, TaskProgressRing } from '../components';
import { Button, Badge, Avatar, SearchInput, Skeleton } from '../../ui';
import { usersAPI } from '../../../services/api';

interface AllTasksViewProps {
  tasks: Task[];
  loading: boolean;
  onTaskClick: (task: Task) => void;
  onTaskComplete: (taskId: number) => void;
  onRefresh: () => void;
}

interface TeamMember {
  id: number;
  email: string;
  firstName?: string;
  lastName?: string;
  taskCount: number;
  completedCount: number;
}

const AllTasksView: React.FC<AllTasksViewProps> = ({
  tasks,
  loading,
  onTaskClick,
  onTaskComplete,
  onRefresh,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMember, setSelectedMember] = useState<number | 'all'>('all');
  const [groupBy, setGroupBy] = useState<'status' | 'priority' | 'assignee'>('status');
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);

  // Calculate team members from tasks
  useEffect(() => {
    const memberMap = new Map<number, TeamMember>();
    
    tasks.forEach(task => {
      task.assignments?.forEach(assignment => {
        const user = assignment.user;
        if (!memberMap.has(user.id)) {
          memberMap.set(user.id, {
            id: user.id,
            email: user.email,
            firstName: user.firstName,
            lastName: user.lastName,
            taskCount: 0,
            completedCount: 0,
          });
        }
        const member = memberMap.get(user.id)!;
        member.taskCount++;
        if (task.status === TaskStatus.COMPLETED) {
          member.completedCount++;
        }
      });
    });

    setTeamMembers(Array.from(memberMap.values()));
  }, [tasks]);

  const toggleGroup = (groupId: string) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      return next;
    });
  };

  // Filter tasks
  const filteredTasks = useMemo(() => {
    let result = tasks;

    // Filter by search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(t =>
        t.title.toLowerCase().includes(query) ||
        t.description?.toLowerCase().includes(query)
      );
    }

    // Filter by team member
    if (selectedMember !== 'all') {
      result = result.filter(t =>
        t.assignments?.some(a => a.user.id === selectedMember)
      );
    }

    return result;
  }, [tasks, searchQuery, selectedMember]);

  // Group tasks
  const groupedTasks = useMemo(() => {
    const groups: Record<string, { title: string; tasks: Task[]; color: string }> = {};

    if (groupBy === 'status') {
      const statusOrder = ['NOT_STARTED', 'IN_PROGRESS', 'COMPLETED', 'DEFERRED', 'CANCELLED'];
      statusOrder.forEach(status => {
        const statusTasks = filteredTasks.filter(t => t.status === status);
        if (statusTasks.length > 0) {
          groups[status] = {
            title: status.replace('_', ' '),
            tasks: statusTasks,
            color: getStatusColor(status as TaskStatus),
          };
        }
      });
    } else if (groupBy === 'priority') {
      const priorityOrder = ['URGENT', 'HIGH', 'MEDIUM', 'NORMAL', 'LOW'];
      priorityOrder.forEach(priority => {
        const priorityTasks = filteredTasks.filter(t => t.priority === priority);
        if (priorityTasks.length > 0) {
          groups[priority] = {
            title: priority,
            tasks: priorityTasks,
            color: getPriorityColor(priority as TaskPriority),
          };
        }
      });
    } else {
      // Group by assignee
      teamMembers.forEach(member => {
        const memberTasks = filteredTasks.filter(t =>
          t.assignments?.some(a => a.user.id === member.id)
        );
        if (memberTasks.length > 0) {
          groups[`member-${member.id}`] = {
            title: member.firstName || member.email,
            tasks: memberTasks,
            color: 'blue',
          };
        }
      });
      // Unassigned tasks
      const unassigned = filteredTasks.filter(t => !t.assignments?.length);
      if (unassigned.length > 0) {
        groups['unassigned'] = {
          title: 'Unassigned',
          tasks: unassigned,
          color: 'gray',
        };
      }
    }

    return groups;
  }, [filteredTasks, groupBy, teamMembers]);

  if (loading) {
    return <LoadingSkeleton />;
  }

  return (
    <div className="space-y-6">
      {/* Team Overview */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <UsersIcon className="h-5 w-5 text-gray-400" />
            Team Overview
          </h2>
        </div>
        {/* Team member chips */}
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setSelectedMember('all')}
            className={clsx(
              'px-4 py-2 rounded-full text-sm font-medium transition-all',
              selectedMember === 'all'
                ? 'bg-primary-100 text-primary-700 ring-2 ring-primary-500'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            )}
          >
            All ({tasks.length})
          </button>
          {teamMembers.map(member => (
            <button
              key={member.id}
              onClick={() => setSelectedMember(member.id)}
              className={clsx(
                'flex items-center gap-2 px-3 py-2 rounded-full text-sm font-medium transition-all',
                selectedMember === member.id
                  ? 'bg-primary-100 text-primary-700 ring-2 ring-primary-500'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              )}
            >
              <Avatar size="xs" name={member.firstName || member.email} />
              <span>{member.firstName || member.email.split('@')[0]}</span>
              <Badge size="sm" variant="neutral">{member.taskCount}</Badge>
            </button>
          ))}
        </div>
      </div>

      {/* Filters & Search */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <SearchInput
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Search tasks..."
            onClear={() => setSearchQuery('')}
          />
        </div>
        <div className="flex gap-2">
          <select
            value={groupBy}
            onChange={(e) => setGroupBy(e.target.value as any)}
            className="px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-700 focus:ring-2 focus:ring-primary-500"
          >
            <option value="status">Group by Status</option>
            <option value="priority">Group by Priority</option>
            <option value="assignee">Group by Assignee</option>
          </select>
        </div>
      </div>

      {/* Grouped Tasks */}
      <div className="space-y-4">
        {Object.entries(groupedTasks).map(([key, group]) => {
          const isCollapsed = collapsedGroups.has(key);
          return (
            <div key={key} className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              <button
                onClick={() => toggleGroup(key)}
                className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className={clsx('w-3 h-3 rounded-full', `bg-${group.color}-500`)} />
                  <h3 className="font-semibold text-gray-900">{group.title}</h3>
                  <Badge size="sm" variant="neutral">{group.tasks.length}</Badge>
                </div>
                {isCollapsed ? (
                  <ChevronRightIcon className="h-5 w-5 text-gray-400" />
                ) : (
                  <ChevronDownIcon className="h-5 w-5 text-gray-400" />
                )}
              </button>
              {!isCollapsed && (
                <div className="px-4 pb-4 space-y-3">
                  {group.tasks.map(task => (
                    <TaskCard
                      key={task.id}
                      task={task}
                      onClick={() => onTaskClick(task)}
                      onComplete={() => onTaskComplete(task.id)}
                      showAssignee
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Empty State */}
      {Object.keys(groupedTasks).length === 0 && (
        <div className="bg-white rounded-2xl p-12 text-center shadow-sm border border-gray-100">
          <CheckCircleIcon className="h-16 w-16 mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No tasks found</h3>
          <p className="text-gray-500">Try adjusting your filters or search query.</p>
        </div>
      )}
    </div>
  );
};

const getStatusColor = (status: TaskStatus): string => {
  const colors: Record<string, string> = {
    NOT_STARTED: 'gray', IN_PROGRESS: 'yellow', COMPLETED: 'green',
    DEFERRED: 'purple', CANCELLED: 'red',
  };
  return colors[status] || 'gray';
};

const getPriorityColor = (priority: TaskPriority): string => {
  const colors: Record<string, string> = {
    LOW: 'gray', NORMAL: 'blue', MEDIUM: 'yellow', HIGH: 'orange', URGENT: 'red',
  };
  return colors[priority] || 'gray';
};

const LoadingSkeleton = () => (
  <div className="space-y-6">
    <Skeleton className="h-40 rounded-2xl" />
    <Skeleton className="h-64 rounded-2xl" />
  </div>
);

export default AllTasksView;

